'use client'

import { useEffect, useRef, useCallback } from 'react'
import { useDemo } from './demo-context'
import { useDemoActions } from './use-demo-actions'

// Simulation configuration
const CONFIG = {
  REQUEST_INTERVAL_MIN: 20000,
  REQUEST_INTERVAL_MAX: 45000,
  UPVOTE_INTERVAL_MIN: 12000,
  UPVOTE_INTERVAL_MAX: 25000,
  BOOST_INTERVAL_MIN: 60000,
  BOOST_INTERVAL_MAX: 120000,
  TIP_INTERVAL_MIN: 90000,
  TIP_INTERVAL_MAX: 180000,
  REACTION_INTERVAL_MIN: 6000,
  REACTION_INTERVAL_MAX: 18000,
  VISITOR_INTERVAL_MIN: 30000,
  VISITOR_INTERVAL_MAX: 60000,
  // Pre-event visitor trickle
  PRE_EVENT_VISITOR_INTERVAL_MIN: 15000,
  PRE_EVENT_VISITOR_INTERVAL_MAX: 30000,
  PRE_EVENT_VISITOR_CAP: 5,

  REQUEST_BASE_PROBABILITY: 0.6,
  REQUEST_MIN_PROBABILITY: 0.0,
  UPVOTE_PROBABILITY: 0.4,
  TIP_PROBABILITY: 0.25,
  MAX_SIMULATED_QUEUE_SIZE: 8,
  VISITOR_MIN: 15,
  VISITOR_MAX: 50,
  BREAK_ACTIVITY_MULTIPLIER: 0.2,
}

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function scheduleNext(
  callback: () => void,
  minInterval: number,
  maxInterval: number,
  multiplier: number = 1
): NodeJS.Timeout {
  const delay = randomBetween(minInterval, maxInterval) * multiplier
  return setTimeout(callback, delay)
}

export function useDemoSimulation() {
  const { state, dispatch } = useDemo()
  const actions = useDemoActions(dispatch)
  const {
    addSimulatedRequest,
    addSimulatedUpvote,
    addSimulatedBoost,
    addSimulatedTip,
    addSimulatedReaction,
    setVisitorCount,
  } = actions

  // Store refs to avoid stale closures
  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state }, [state])

  // Track previous states for reactive behaviors
  const prevNowPlayingRef = useRef(state.nowPlaying)
  const prevBreakStatusRef = useRef(state.breakStatus)

  // Timer refs for cleanup
  const requestTimerRef = useRef<NodeJS.Timeout | null>(null)
  const upvoteTimerRef = useRef<NodeJS.Timeout | null>(null)
  const boostTimerRef = useRef<NodeJS.Timeout | null>(null)
  const tipTimerRef = useRef<NodeJS.Timeout | null>(null)
  const reactionTimerRef = useRef<NodeJS.Timeout | null>(null)
  const visitorTimerRef = useRef<NodeJS.Timeout | null>(null)

  const getMultiplier = useCallback((): number => {
    return stateRef.current.breakStatus.onBreak ? (1 / CONFIG.BREAK_ACTIVITY_MULTIPLIER) : 1
  }, [])

  const getRequestProbability = useCallback((): number => {
    const queueSize = stateRef.current.queue.length
    const maxSize = CONFIG.MAX_SIMULATED_QUEUE_SIZE
    if (queueSize >= maxSize) return 0
    const ratio = queueSize / maxSize
    const probability = CONFIG.REQUEST_BASE_PROBABILITY * Math.pow(1 - ratio, 2)
    return Math.max(CONFIG.REQUEST_MIN_PROBABILITY, probability)
  }, [])

  // ─── Live simulation loops ──────────────────────────────────────────

  const scheduleRequest = useCallback(() => {
    const runRequest = () => {
      if (stateRef.current.phase !== 'live') return
      if (stateRef.current.settings.requestsEnabled && Math.random() < getRequestProbability()) {
        addSimulatedRequest()
      }
      requestTimerRef.current = scheduleNext(runRequest, CONFIG.REQUEST_INTERVAL_MIN, CONFIG.REQUEST_INTERVAL_MAX, getMultiplier())
    }
    requestTimerRef.current = scheduleNext(runRequest, CONFIG.REQUEST_INTERVAL_MIN, CONFIG.REQUEST_INTERVAL_MAX)
  }, [addSimulatedRequest, getMultiplier, getRequestProbability])

  const scheduleUpvote = useCallback(() => {
    const runUpvote = () => {
      if (stateRef.current.phase !== 'live') return
      if (stateRef.current.queue.length > 0 && Math.random() < CONFIG.UPVOTE_PROBABILITY) {
        addSimulatedUpvote()
      }
      upvoteTimerRef.current = scheduleNext(runUpvote, CONFIG.UPVOTE_INTERVAL_MIN, CONFIG.UPVOTE_INTERVAL_MAX, getMultiplier())
    }
    upvoteTimerRef.current = scheduleNext(runUpvote, CONFIG.UPVOTE_INTERVAL_MIN, CONFIG.UPVOTE_INTERVAL_MAX)
  }, [addSimulatedUpvote, getMultiplier])

  const scheduleBoost = useCallback(() => {
    const runBoost = () => {
      if (stateRef.current.phase !== 'live') return
      if (stateRef.current.settings.tipsEnabled && stateRef.current.queue.length > 0) {
        addSimulatedBoost()
      }
      boostTimerRef.current = scheduleNext(runBoost, CONFIG.BOOST_INTERVAL_MIN, CONFIG.BOOST_INTERVAL_MAX, getMultiplier())
    }
    boostTimerRef.current = scheduleNext(runBoost, CONFIG.BOOST_INTERVAL_MIN, CONFIG.BOOST_INTERVAL_MAX)
  }, [addSimulatedBoost, getMultiplier])

  const scheduleTip = useCallback(() => {
    const runTip = () => {
      if (stateRef.current.phase !== 'live') return
      if (stateRef.current.settings.tipsEnabled && Math.random() < CONFIG.TIP_PROBABILITY) {
        addSimulatedTip()
      }
      tipTimerRef.current = scheduleNext(runTip, CONFIG.TIP_INTERVAL_MIN, CONFIG.TIP_INTERVAL_MAX, getMultiplier())
    }
    tipTimerRef.current = scheduleNext(runTip, CONFIG.TIP_INTERVAL_MIN, CONFIG.TIP_INTERVAL_MAX)
  }, [addSimulatedTip, getMultiplier])

  const scheduleReaction = useCallback(() => {
    const runReaction = () => {
      if (stateRef.current.phase !== 'live') return
      if (stateRef.current.settings.reactionsEnabled && stateRef.current.nowPlaying) {
        addSimulatedReaction()
      }
      reactionTimerRef.current = scheduleNext(runReaction, CONFIG.REACTION_INTERVAL_MIN, CONFIG.REACTION_INTERVAL_MAX, getMultiplier())
    }
    reactionTimerRef.current = scheduleNext(runReaction, CONFIG.REACTION_INTERVAL_MIN, CONFIG.REACTION_INTERVAL_MAX)
  }, [addSimulatedReaction, getMultiplier])

  const scheduleVisitorUpdate = useCallback(() => {
    const runVisitorUpdate = () => {
      const s = stateRef.current

      if (s.phase === 'pre-event') {
        // Slow trickle during pre-event
        if (s.visitorCount < CONFIG.PRE_EVENT_VISITOR_CAP && Math.random() < 0.5) {
          setVisitorCount(s.visitorCount + 1)
        }
        visitorTimerRef.current = scheduleNext(
          runVisitorUpdate,
          CONFIG.PRE_EVENT_VISITOR_INTERVAL_MIN,
          CONFIG.PRE_EVENT_VISITOR_INTERVAL_MAX
        )
        return
      }

      const isPlaying = !!s.nowPlaying
      const onBreak = s.breakStatus.onBreak
      let bias = 0
      if (isPlaying) bias = 0.2
      if (onBreak) bias = -0.3

      const change = Math.random() < (0.5 + bias) ? 1 : -1
      const variance = Math.random() < 0.3 ? 2 : 1
      const newCount = Math.max(
        CONFIG.VISITOR_MIN,
        Math.min(CONFIG.VISITOR_MAX, s.visitorCount + change * variance)
      )
      if (newCount !== s.visitorCount) {
        setVisitorCount(newCount)
      }
      visitorTimerRef.current = scheduleNext(runVisitorUpdate, CONFIG.VISITOR_INTERVAL_MIN, CONFIG.VISITOR_INTERVAL_MAX)
    }
    visitorTimerRef.current = scheduleNext(runVisitorUpdate, CONFIG.VISITOR_INTERVAL_MIN, CONFIG.VISITOR_INTERVAL_MAX)
  }, [setVisitorCount])

  // ─── Burst of initial requests after going live ────────────────────

  const prevPhaseRef = useRef(state.phase)
  useEffect(() => {
    if (state.phase === 'live' && prevPhaseRef.current === 'pre-event') {
      // Stagger 3 requests — let the empty queue sit for a bit first
      const delays = [12000, 25000, 40000]
      const timers = delays.map(delay =>
        setTimeout(() => {
          if (stateRef.current.settings.requestsEnabled) {
            addSimulatedRequest()
          }
        }, delay)
      )
      // Also add a tip after a bit
      const tipTimer = setTimeout(() => {
        if (stateRef.current.settings.tipsEnabled) {
          addSimulatedTip()
        }
      }, 50000)

      prevPhaseRef.current = state.phase
      return () => {
        timers.forEach(clearTimeout)
        clearTimeout(tipTimer)
      }
    }
    prevPhaseRef.current = state.phase
  }, [state.phase, addSimulatedRequest, addSimulatedTip])

  // ─── Reactive behaviors ─────────────────────────────────────────────

  useEffect(() => {
    const { nowPlaying } = stateRef.current
    if (stateRef.current.phase !== 'live') {
      prevNowPlayingRef.current = nowPlaying
      return
    }

    if (nowPlaying && !prevNowPlayingRef.current) {
      const burstCount = 2 + Math.floor(Math.random() * 3)
      let delay = 0
      for (let i = 0; i < burstCount; i++) {
        delay += randomBetween(500, 2000)
        setTimeout(() => {
          if (stateRef.current.settings.reactionsEnabled) addSimulatedReaction()
        }, delay)
      }
      if (Math.random() < 0.3 && stateRef.current.settings.requestsEnabled && stateRef.current.queue.length < CONFIG.MAX_SIMULATED_QUEUE_SIZE - 2) {
        setTimeout(addSimulatedRequest, randomBetween(8000, 20000))
      }
    }

    if (!nowPlaying && prevNowPlayingRef.current) {
      const applauseCount = 1 + Math.floor(Math.random() * 3)
      let delay = 0
      for (let i = 0; i < applauseCount; i++) {
        delay += randomBetween(200, 800)
        setTimeout(() => {
          if (stateRef.current.settings.reactionsEnabled) addSimulatedReaction()
        }, delay)
      }
      if (stateRef.current.queue.length > 0 && Math.random() < 0.6) {
        setTimeout(addSimulatedUpvote, randomBetween(1000, 3000))
      }
    }

    prevNowPlayingRef.current = nowPlaying
  }, [state.nowPlaying, addSimulatedReaction, addSimulatedRequest, addSimulatedUpvote, state.phase])

  useEffect(() => {
    if (stateRef.current.phase !== 'live') {
      prevBreakStatusRef.current = state.breakStatus
      return
    }

    if (!state.breakStatus.onBreak && prevBreakStatusRef.current.onBreak) {
      const burstCount = 4 + Math.floor(Math.random() * 4)
      let delay = 0
      for (let i = 0; i < burstCount; i++) {
        delay += randomBetween(300, 1000)
        setTimeout(() => {
          if (stateRef.current.settings.reactionsEnabled) addSimulatedReaction()
        }, delay)
      }
    }
    prevBreakStatusRef.current = state.breakStatus
  }, [state.breakStatus, addSimulatedReaction, state.phase])

  // ─── Start loops on mount ───────────────────────────────────────────

  useEffect(() => {
    scheduleRequest()
    scheduleUpvote()
    scheduleBoost()
    scheduleTip()
    scheduleReaction()
    scheduleVisitorUpdate()

    return () => {
      if (requestTimerRef.current) clearTimeout(requestTimerRef.current)
      if (upvoteTimerRef.current) clearTimeout(upvoteTimerRef.current)
      if (boostTimerRef.current) clearTimeout(boostTimerRef.current)
      if (tipTimerRef.current) clearTimeout(tipTimerRef.current)
      if (reactionTimerRef.current) clearTimeout(reactionTimerRef.current)
      if (visitorTimerRef.current) clearTimeout(visitorTimerRef.current)
    }
  }, [scheduleRequest, scheduleUpvote, scheduleBoost, scheduleTip, scheduleReaction, scheduleVisitorUpdate])
}
